import type { MapAddressSuggestion } from "features/dt-basic-setup/single-building/model/types";

import { searchAddressSuggestions } from "features/dt-basic-setup/single-building/api/geocoding";
import { useEffect, useRef, useState } from "react";

import { ADDRESS_DEBOUNCE_MS } from "../constants";

export const useSetOptionsOnInputChangeEffect = (
  setOptions: (options: MapAddressSuggestion[]) => void,
  inputValue: string,
  MIN_QUERY_LENGTH: number
) => {
  const [loading, setLoading] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const latestRequestNo = useRef(0);

  useEffect(() => {
    const q = inputValue.trim();

    abortRef.current?.abort();
    const requestNo = ++latestRequestNo.current;
    setOptions([]);

    if (q.length < MIN_QUERY_LENGTH) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const timer = window.setTimeout(async () => {
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const next = await searchAddressSuggestions(q, ac.signal);
        if (requestNo !== latestRequestNo.current || ac.signal.aborted) return;
        setOptions(next);
      } catch {
        if (requestNo !== latestRequestNo.current || ac.signal.aborted) return;
        setOptions([]);
      } finally {
        if (requestNo === latestRequestNo.current && !ac.signal.aborted) setLoading(false);
      }
    }, ADDRESS_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [inputValue, setOptions, MIN_QUERY_LENGTH]);

  return { loading };
};
